import { Button, Checkbox, LoadSpinner, TextArea } from '@calizahq/react-components-v2'
import debounce from 'debounce-promise'
import { Form, Formik } from 'formik'
import React, { useEffect, useRef, useState } from 'react'

import { InputFile, InputSelect } from '@/components'
import { useUserContext } from '@/context'
import { TicketMutations } from '@/graphql/mutations/ticket.mutations'
import { TicketHomeAreas } from '@/graphql/queries/ticketHomeAreas.queries'
import { useMobile } from '@/hooks'
import { useGraphql, useGraphqlMutation } from '@/hooks/useGraphql'
import useTicketReasons from '@/hooks/useTicketReasons/useTicketReasons'
import useTicketTypes from '@/hooks/useTicketTypes/useTicketTypes'
import { NewTicketDetails, Type } from '@/types/Ticket'
import { TicketHomeArea } from '@/types/TicketHomeArea'

import { CreateConfirmation } from './CreateConfirmation'
import {
  formSchema,
  FormValuesType,
  initialValues,
  Option,
  Options,
  searchHome,
  searchMember,
  searchReservation,
  searchReservationByUser,
} from './CreateTicket.util'

const debouncedSearchRes = debounce(searchReservation, 1000, { leading: true })
const debouncedSearchResByUser = debounce(searchReservationByUser, 1000, { leading: true })
const debouncedSearchHome = debounce(searchHome, 1000, { leading: true })
const debouncedSearchMember = debounce(searchMember, 1000, { leading: true })

type Props = {
  contactId?: number
  homeId?: number
  memberId?: number
  parentTicketId?: number
  refetchOnSuccess?: () => void
  reservationId?: number
  toggle: () => void
}

export const CreateTicketForm = ({
  toggle,
  contactId,
  homeId,
  reservationId,
  memberId,
  parentTicketId,
  refetchOnSuccess,
}: Props) => {
  const [error, setError] = useState<string>()
  const [ticket, setTicket] = useState<NewTicketDetails>()
  const [contactDefaultReservationOptions, setContactDefaultReservationOptions] = useState<Options>([])
  const [contactDefaultHomeOptions, setContactDefaultHomeOptions] = useState<Options>([])
  const [autoReservationId, setAutoReservationId] = useState<number | undefined>()
  const [autoHomeId, setAutoHomeId] = useState<number | undefined>()
  const userClearedReservation = useRef(false)
  const userClearedHome = useRef(false)
  const { user } = useUserContext()

  const { isMobile } = useMobile()

  const { data: typesData, isPending: isTicketTypeLoading } = useTicketTypes()
  const { data: reasonsData, isPending: isTicketReasonsLoading } = useTicketReasons()
  const { data: homeAreasData, isPending: isTicketHomeAreasLoading } = useGraphql<{
    ticketHomeAreas: TicketHomeArea[]
  }>(TicketHomeAreas.QUERY_KEY, TicketHomeAreas.getTicketHomeAreas())

  useEffect(() => {
    if (!contactId || homeId || reservationId) return
    searchReservationByUser(contactId, '').then((results) => {
      setContactDefaultReservationOptions(results)
      const seenHomeIds = new Set<number>()
      const homes: Options = []
      results.forEach((r: Option & { homeId?: number; homeLabel?: string }) => {
        if (r.homeId && !seenHomeIds.has(r.homeId)) {
          seenHomeIds.add(r.homeId)
          homes.push({ label: r.homeLabel || '', value: r.homeId })
        }
      })
      setContactDefaultHomeOptions(homes)
      const current = (results as Array<Option & { homeId?: number; status?: string }>).find(
        (r) => r.status === 'current',
      )
      const autoRes = current ?? (results.length === 1 ? (results[0] as Option & { homeId?: number }) : undefined)
      if (autoRes) {
        setAutoReservationId(autoRes.value as number)
        if (autoRes.homeId) setAutoHomeId(autoRes.homeId)
      }
    })
  }, [contactId, homeId, reservationId])

  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(TicketMutations.createTicket(), {
    onSuccess: (res: { createTicket: NewTicketDetails }) => {
      setTicket(res.createTicket)
      refetchOnSuccess && refetchOnSuccess()
    },
  })

  const ticketTypeOptions: Options =
    typesData && typesData.ticketTypes
      ? typesData.ticketTypes
          .filter((type) => !parentTicketId || !type.allowAsParent)
          .map((type) => {
            return { label: type.description, value: type.id }
          })
      : []

  const ticketHomeAreasOptions: Options =
    homeAreasData && homeAreasData.ticketHomeAreas
      ? homeAreasData.ticketHomeAreas.map((area) => {
          return { label: area.description, value: area.id }
        })
      : []

  ticketTypeOptions.unshift({ label: 'Select Type', value: '' })

  let ticketReasonsOptions: Options = [{ label: 'Select Reason', value: '' }]
  const ticketReasons = reasonsData && reasonsData.ticketReasons ? reasonsData.ticketReasons : []

  const isLoading = isTicketTypeLoading || isTicketReasonsLoading || isTicketHomeAreasLoading

  const handleSubmit = (values: FormValuesType) => {
    const { contactId, homeId } = values
    const { attachments, ticketType, ...restOfValues } = values
    if (ticketType === Type.BILLING) {
      if (homeId) delete restOfValues.homeId
    } else {
      if (contactId) delete restOfValues.contactId
    }
    if (ticketType === Type.RESERVATION) {
      if (homeId) delete restOfValues.homeId
      if (contactId) delete restOfValues.contactId
    }
    if (ticketType !== Type.MAINTENANCE) {
      restOfValues.permissionToEnter = false
      restOfValues.ticketHomeAreaId = undefined
    }
    mutate({
      ticket: {
        ...restOfValues,
        creatorId: user?.id,
      },
      attachments,
    })
  }

  const loadReservationOptios = (inputValue) =>
    contactId && !reservationId ? debouncedSearchResByUser(contactId, inputValue) : debouncedSearchRes(inputValue)
  const loadHomeOptions = (inputValue) => {
    if (contactId && contactDefaultHomeOptions.length > 0) {
      const filtered = contactDefaultHomeOptions.filter(
        (h) => !inputValue || String(h.label).toLowerCase().includes(inputValue.toLowerCase()),
      )
      return Promise.resolve(filtered)
    }
    return debouncedSearchHome(inputValue)
  }
  const loadMemberOptions = (inputValue) => debouncedSearchMember(inputValue)

  return (
    <>
      {isLoading ? (
        <div className="flex h-48 w-full items-center justify-center ">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <>
          {!ticket ? (
            <Formik
              enableReinitialize={true}
              initialValues={{
                ...initialValues,
                homeId: homeId ?? autoHomeId,
                reservationId: reservationId ?? autoReservationId,
                contactId: memberId,
                parentTicketId: parentTicketId,
              }}
              onSubmit={handleSubmit}
              validateOnBlur={false}
              validationSchema={formSchema}
            >
              {({ values, setFieldValue }) => {
                const { ticketType } = values

                function handleTypeChange(val: any) {
                  const newTicketType =
                    typesData && typesData.ticketTypes
                      ? typesData.ticketTypes.find((type) => type.id === val)?.description
                      : ''

                  setFieldValue('ticketType', newTicketType)

                  ticketReasonsOptions = ticketReasons
                    .filter((reason) => reason.ticketTypeId === val)
                    .map((reason) => {
                      return { label: reason.description, value: reason.id }
                    })
                  ticketReasonsOptions.unshift({ label: 'Select Reason', value: '' })

                  if (newTicketType === Type.CONVERSATION) {
                    const defaultReason = ticketReasons.find(
                      (reason) => reason.ticketTypeId === val && reason.description === 'Mobile Chat',
                    )
                    setFieldValue('ticketReasonId', defaultReason?.id ?? '')
                  } else {
                    setFieldValue('ticketReasonId', '')
                  }

                  // Sync field values to match what the new type shows/hides.
                  // Restore prop-supplied or auto-selected defaults when switching
                  // back to a type that shows the field; clear when switching away.
                  const newIsConversation = newTicketType === Type.CONVERSATION
                  const newIsBilling = newTicketType === Type.BILLING
                  const newIsReservation = newTicketType === Type.RESERVATION
                  const newShowsReservation = !newIsConversation
                  const newShowsHome = !newIsReservation && !newIsBilling && !newIsConversation
                  const newShowsMember = newIsBilling

                  setFieldValue(
                    'reservationId',
                    newShowsReservation && !userClearedReservation.current
                      ? (reservationId ?? autoReservationId)
                      : undefined,
                  )
                  setFieldValue('homeId', newShowsHome && !userClearedHome.current ? (homeId ?? autoHomeId) : undefined)
                  setFieldValue('contactId', newShowsMember ? memberId : undefined)
                }

                function handleReservationChange() {
                  userClearedReservation.current = true
                  userClearedHome.current = true
                  setFieldValue('contactId', undefined)
                  setFieldValue('homeId', undefined)
                  setError(undefined)
                }

                function resetReservation() {
                  userClearedHome.current = true
                  setFieldValue('reservationId', undefined)
                  setError(undefined)
                }

                const isConversation = ticketType === Type.CONVERSATION
                const isReservationSelected = ticketType === Type.RESERVATION
                const showHomeSelection = !isReservationSelected && ticketType !== Type.BILLING && !isConversation
                const showMemberSelection = !isReservationSelected && ticketType === Type.BILLING

                return (
                  <Form className="flex w-full flex-col flex-wrap px-6 md:max-w-120">
                    <p className="leading-nonemt-4 mb-4 font-bold text-header-md">New Ticket</p>
                    <div className="flex w-full flex-col md:mb-4 md:grid md:grid-cols-2 md:gap-4">
                      <InputSelect
                        className=" mb-4 w-full md:mb-0 md:mr-4"
                        id="ticketTypeId"
                        label="Type"
                        name="ticketTypeId"
                        onChange={handleTypeChange}
                        options={ticketTypeOptions}
                      ></InputSelect>
                      {!isConversation && (
                        <InputSelect
                          className="mb-4 w-full md:mb-0"
                          disabled={!ticketType || ticketType === ''}
                          id="ticketReasonId"
                          label="Reason"
                          name="ticketReasonId"
                          options={ticketReasonsOptions}
                        ></InputSelect>
                      )}
                    </div>
                    {parentTicketId && (
                      <div className="flex w-full flex-col md:mb-4 md:grid md:grid-cols-2 md:gap-4">
                        <div className="mb-4 w-full md:mb-0">
                          <p className="mb-1 text-p-sm text-tertiary">Parent Ticket</p>
                          <p className="font-bold">#{parentTicketId}</p>
                        </div>
                      </div>
                    )}
                    {!isConversation && (
                      <div className="flex w-full flex-col md:mb-4 md:grid md:grid-cols-2 md:gap-4">
                        <InputSelect
                          async={true}
                          className={`mb-4 w-full md:mb-0 ${isReservationSelected ? '' : 'md:mr-4'}`}
                          defaultOptions={
                            contactId && !reservationId && contactDefaultReservationOptions.length > 0
                              ? contactDefaultReservationOptions
                              : false
                          }
                          id="reservationId"
                          label="Reservation"
                          loadOptions={loadReservationOptios}
                          name="reservationId"
                          onChange={handleReservationChange}
                          optionValue={
                            reservationId
                              ? { label: reservationId + '' || '', value: reservationId }
                              : autoReservationId
                                ? { label: autoReservationId + '', value: autoReservationId }
                                : undefined
                          }
                          showValueAsLabel={true}
                        />

                        <>
                          {showHomeSelection && (
                            <InputSelect
                              async={true}
                              className=" mb-4 w-full md:mb-0"
                              defaultOptions={
                                contactId && !homeId && contactDefaultHomeOptions.length > 0
                                  ? contactDefaultHomeOptions
                                  : false
                              }
                              id="homeId"
                              label="Home"
                              loadOptions={loadHomeOptions}
                              name="homeId"
                              onChange={resetReservation}
                              optionValue={
                                homeId
                                  ? { label: homeId + '' || '', value: homeId }
                                  : autoHomeId
                                    ? { label: autoHomeId + '', value: autoHomeId }
                                    : undefined
                              }
                            ></InputSelect>
                          )}
                          {showMemberSelection && (
                            <InputSelect
                              async={true}
                              className="mb-4 w-full md:mb-0"
                              defaultOptions={true}
                              id="contactId"
                              label="Member"
                              loadOptions={loadMemberOptions}
                              name="contactId"
                              onChange={resetReservation}
                              optionValue={memberId ? { label: memberId + '' || '', value: memberId } : undefined}
                            />
                          )}
                        </>
                      </div>
                    )}
                    {ticketType === Type.MAINTENANCE && (
                      <div className="flex w-full flex-col md:mb-4 md:grid md:grid-cols-2 md:gap-4">
                        <Checkbox
                          className="mb-4 w-full md:mb-0 md:mr-4"
                          id="permissionToEnter"
                          label="Permission to enter"
                          name="permissionToEnter"
                        />
                        <InputSelect
                          className="mb-4 w-full md:mb-0"
                          id="ticketHomeAreaId"
                          label="Home Area"
                          name="ticketHomeAreaId"
                          options={ticketHomeAreasOptions}
                        ></InputSelect>
                      </div>
                    )}

                    <TextArea className="mb-4 w-full" label="Add description" name="description" />

                    <InputFile
                      accept={{
                        'image/jpeg': [],
                        'image/png': [],
                        'application/pdf': [],
                      }}
                      className="mb-4"
                      gridClassName="grid-cols-3 md:grid-cols-5"
                      id="attachments"
                      isPreview={true}
                      maxFiles={6}
                      maxSize={15000000}
                      name="attachments"
                    />

                    {error && <p className=" mb-4 text-p-sm text-text-semantic-error">{error}</p>}
                    <div className="flex flex-row items-center justify-between">
                      {isMobile ? (
                        <Button className="mr-4 flex-1 bg-grey-100" onClick={toggle} variant="secondary">
                          Close
                        </Button>
                      ) : (
                        <Button onClick={toggle} size="small" variant="secondary">
                          <span className="underline underline-offset-2">Close</span>
                        </Button>
                      )}
                      <Button
                        className="flex-1 bg-text-semantic-success md:flex-0 md:w-auto"
                        disabled={isMutationLoading}
                        isLoading={isMutationLoading}
                        size={isMobile ? undefined : 'small'}
                        type="submit"
                      >
                        Create
                      </Button>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          ) : (
            <CreateConfirmation ticket={ticket} toggle={toggle} />
          )}
        </>
      )}
    </>
  )
}
